import React, { useEffect, useMemo } from 'react';
import { reduxForm, InjectedFormProps, reset } from 'redux-form';
import { Form, Card, Row, Select, Button, Spin, Col } from 'antd';
import {
  FormField,
  SkeletonHOC,
  InputTypes,
  getFilterOption,
  getOptions,
  required,
  Nullable,
} from 'common';
import { compose } from 'redux';
import globalStyles from 'common/global.module.scss';
import {
  TAXTRAX_FORM,
  TaxTraxFormLabels,
  LocationFormNames,
  TaxTraxFormPlaceholders,
  TAB_INDEX,
  EMPTY_VALUE,
  TAXTRAX_FORM_INITIAL_VALUES,
  SearchTypeFormNames,
} from './TaxTrax.constants';
import {
  getCountyOptions,
  getCities,
  getVillageOptions,
  getCityOptions,
  getCounties,
  getVillages,
} from './TaxTrax.selectors';
import { useDispatch } from 'react-redux';
import TaxTraxFormTabs from './TaxTraxFormTabs';
import { AvailableMunicipalities, GetDriversResult } from './models';
import {
  getAvailableMunicipalities,
  getDrivers,
  getStateOptions,
  resetDriversData,
  resetResults,
  setAvailableMunicipalitiesModalVisibility,
} from '.';
import { Redirect } from 'react-router-dom';
import { LoginState } from 'pages/Login';
import { USState } from 'common/models';
import TaxTraxAvailableMunicipiliaties from './TaxTraxAvailableMunicipalities/TaxTraxAvailableMunicipalities';
import { useAccount, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { getAccessToken } from 'common/helpers';

const { Option } = Select;
interface TaxTraxFormProps {
  login: LoginState;
  usStates: USState[];
  driversData: GetDriversResult;
  handleSubmit: any;
  loading: boolean;
  formValues: any;
  stateHob: string;
  countyHob: string;
  cityHob: string;
  showAvailableMunicipalitiesModal: boolean;
  availableMunicipalitiesLoading: boolean;
  availableMunicipalities: AvailableMunicipalities;
}

type Props = TaxTraxFormProps &
  InjectedFormProps<
    { change: any; handleSubmit: any; valid: boolean },
    TaxTraxFormProps
  >;

const TaxTraxForm: React.FC<Props> = ({
  change,
  handleSubmit,
  login,
  usStates,
  loading,
  driversData,
  formValues,
  valid,
  stateHob,
  countyHob,
  cityHob,
  showAvailableMunicipalitiesModal,
  availableMunicipalitiesLoading,
  availableMunicipalities,
}) => {
  const dispatch = useDispatch();
  const counties = useMemo(
    () => getCounties(usStates, stateHob),
    [usStates, stateHob],
  );
  const cities = useMemo(
    () => getCities(counties, countyHob),
    [counties, countyHob],
  );
  const villages = useMemo(
    () => getVillages(cities, cityHob),
    [cities, cityHob],
  );
  const stateOptions = useMemo(() => getStateOptions(usStates), [usStates]);
  const countyOptions = useMemo(
    () => getCountyOptions(counties),
    [counties, stateHob],
  );
  const cityOptions = useMemo(
    () => getCityOptions(cities),
    [cities, countyHob],
  );
  const villageOptions = useMemo(
    () => getVillageOptions(villages),
    [villages, cityHob],
  );
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});

  const clearDrivers = React.useCallback(() => {
    dispatch(change('driversSearch', {}));
  }, [dispatch]);

  const clearField = (fieldName: string) => {
    dispatch(change(fieldName, EMPTY_VALUE));
  };

  const handleFieldFocus = ({ target: { name } }: any) => {
    dispatch(change(SearchTypeFormNames.SEARCH_BY, name.match(/([^.]+)/)[0]));
  };

  const handleStateChange = (value: string) => {
    clearField(LocationFormNames.COUNTY);
    clearField(LocationFormNames.CITY);
    clearField(LocationFormNames.VILLAGE);
    clearDrivers();
    dispatch(resetDriversData());
  };

  const handleCountyChange = (value: string) => {
    clearField(LocationFormNames.CITY);
    clearField(LocationFormNames.VILLAGE);
    clearDrivers();
    if (!value) {
      dispatch(resetDriversData());
      return;
    }
    if (account && inProgress === InteractionStatus.None) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken)
          dispatch(getDrivers(value, response.accessToken));
      });
    }
  };

  const handleCityChange = (value: string) => {
    clearField(LocationFormNames.VILLAGE);
    clearDrivers();
    if (account && inProgress === InteractionStatus.None && value) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken)
          dispatch(getDrivers(value, response.accessToken));
      });
    }
  };

  const handleVillageChange = (value: string) => {
    clearDrivers();
    if (account && inProgress === InteractionStatus.None && value) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken)
          dispatch(getDrivers(value, response.accessToken));
      });
    }
  };

  const handleResetForm = () => {
    dispatch(resetResults());
    dispatch(reset(TAXTRAX_FORM));
    dispatch(resetDriversData());
  };

  const onShowAvailableMunicipalities = () => {
    dispatch(setAvailableMunicipalitiesModalVisibility(true));
    if (
      account &&
      inProgress === InteractionStatus.None &&
      !availableMunicipalities
    ) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken)
          dispatch(getAvailableMunicipalities(response.accessToken));
      });
    }
  };

  // set the state or county dropdown if it has only 1 available option respectively
  useEffect(() => {
    if (counties?.length === 1 && countyHob != counties[0].hobCode) {
      dispatch(change(LocationFormNames.COUNTY, counties[0].hobCode));
      handleCountyChange(counties[0].hobCode);
    } else if (usStates?.length === 1 && stateHob != usStates[0].stateCode) {
      dispatch(change(LocationFormNames.STATE, usStates[0].stateCode));
      handleStateChange(usStates[0].stateCode);
    }
  }, [usStates, counties]);

  if (login.user?.licenseflag == 'False') {
    return <Redirect to={`/pages/license`} />;
  }

  return (
    <Card size="small">
      <Spin spinning={loading} size="large" tip="Loading...">
        {/* TODO consider refactoring this form since most of it is shared with PropertySearchForm.tsx */}
        <Form layout="vertical" data-cy={'property-search-form'}>
          <Row type="flex" align="middle">
            <FormField
              validate={[required]}
              tabIndex={TAB_INDEX}
              label={TaxTraxFormLabels.STATE}
              name={LocationFormNames.STATE}
              placeholder={TaxTraxFormPlaceholders.STATE}
              inputType={InputTypes.SELECT}
              className={`${globalStyles.select} ${globalStyles.input}`}
              allowClear={false}
              onChange={(value: string) => handleStateChange(value)}
              showSearch={true}
              filterOption={(input: any, { props: { children } }: any) =>
                getFilterOption(children, input)
              }
            >
              {getOptions(Option, stateOptions)}
            </FormField>
            <FormField
              validate={[required]}
              tabIndex={TAB_INDEX}
              label={TaxTraxFormLabels.COUNTY}
              name={LocationFormNames.COUNTY}
              placeholder={TaxTraxFormPlaceholders.COUNTY}
              inputType={InputTypes.SELECT}
              className={`${globalStyles.select} ${globalStyles.input}`}
              allowClear={false}
              disabled={!stateHob || !counties}
              onChange={(value: string) => handleCountyChange(value)}
              showSearch={true}
              filterOption={(input: any, { props: { children } }: any) =>
                getFilterOption(children, input)
              }
            >
              {getOptions(Option, countyOptions)}
            </FormField>
            <FormField
              tabIndex={TAB_INDEX}
              label={TaxTraxFormLabels.CITY}
              name={LocationFormNames.CITY}
              placeholder={TaxTraxFormPlaceholders.CITY}
              inputType={InputTypes.SELECT}
              className={`${globalStyles.select} ${globalStyles.input}`}
              allowClear={false}
              disabled={!countyHob || !cities || cityOptions.length < 2}
              onChange={(value: string) => {
                clearField(LocationFormNames.VILLAGE);
                handleCityChange(value);
              }}
              showSearch={true}
              filterOption={(input: any, { props: { children } }: any) =>
                getFilterOption(children, input)
              }
            >
              {getOptions(Option, cityOptions)}
            </FormField>
            <FormField
              tabIndex={TAB_INDEX}
              label={TaxTraxFormLabels.VILLAGE}
              name={LocationFormNames.VILLAGE}
              placeholder={TaxTraxFormPlaceholders.VILLAGE}
              inputType={InputTypes.SELECT}
              className={`${globalStyles.select} ${globalStyles.input}`}
              allowClear={false}
              disabled={!cityHob || !villages || villageOptions.length < 2}
              onChange={(value: string) => handleVillageChange(value)}
              showSearch={true}
              filterOption={(input: any, { props: { children } }: any) =>
                getFilterOption(children, input)
              }
            >
              {getOptions(Option, villageOptions)}
            </FormField>
            <Col>
              <strong>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onShowAvailableMunicipalities()}
                >
                  TaxTrax - Available Municipalities
                </a>
              </strong>
            </Col>
          </Row>
          <TaxTraxFormTabs {...{ handleFieldFocus, driversData }} />
          <Button
            type="ghost"
            onClick={handleResetForm}
            className={globalStyles.buttonWithTopMargin}
          >
            Reset
          </Button>
          <Button
            icon="search"
            disabled={!valid}
            type="primary"
            htmlType="submit"
            onClick={() => handleSubmit(formValues)}
            className={globalStyles.buttonWithTopMargin}
          >
            Search
          </Button>
        </Form>
        <TaxTraxAvailableMunicipiliaties
          isVisible={showAvailableMunicipalitiesModal}
          loading={availableMunicipalitiesLoading}
          municipalities={availableMunicipalities}
        />
      </Spin>
    </Card>
  );
};

export default compose(
  SkeletonHOC({ initProp: 'usStates', rows: 12 }),
  reduxForm<any, TaxTraxFormProps>({
    form: TAXTRAX_FORM,
    initialValues: TAXTRAX_FORM_INITIAL_VALUES,
  }),
)(React.memo(TaxTraxForm));
